import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import { entitlementsClient } from '@/api/clients/entitlementsClient';
import { NetworkError, ServerError } from '@/api/errors';
import { type Entitlements } from '@/api/types/entitlementsTypes';
import { useUserInfo } from '@/features/auth/hooks/useUserInfo';
import { isTokenAuthenticated } from '@/features/auth/isTokenAuthenticated';

export const useUserEntitlementsQueryKey = 'useUserEntitlementsQuery';

export function useUserEntitlementsQuery(): UseQueryResult<Entitlements> {
  const { accessToken } = useUserInfo();
  const isAuthenticated = isTokenAuthenticated(accessToken);

  return useQuery({
    retry: (failureCount, error) => {
      switch (error.constructor) {
        case NetworkError:
        case ServerError:
          return failureCount < 3;
      }

      return false;
    },
    throwOnError: false,
    queryKey: [useUserEntitlementsQueryKey, accessToken],
    queryFn: async () => {
      const userEntitlements = await entitlementsClient.getUserEntitlements();

      return userEntitlements;
    },
    // Need to get entitlements based on access token, supporting anonymous, mvpd, profile users
    enabled: Boolean(accessToken) && isAuthenticated,
  });
}
